import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';

export type FeatureId = 
  | 'basic_builder'
  | 'save_projects'
  | 'export_pdf'
  | 'custom_pdf_branding'
  | 'my_gear'
  | 'priority_support'
  | 'team_sharing'
  | 'api_access'
  | 'version_tracking'
  | 'rental_pricing';

export const useHasFeature = () => {
  const { user, roles } = useAuth();
  const { limits } = usePlanLimits();

  // Determine user's effective role for feature access
  const effectiveRole = useMemo(() => {
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('business')) return 'business';
    if (roles.includes('pro')) return 'pro';
    if (roles.includes('moderator')) return 'moderator';
    return 'user'; // Free tier
  }, [roles]);

  // Get features for the user's role
  const userFeatures = useMemo(() => {
    const planLimit = limits.find(l => l.role === effectiveRole);
    return planLimit?.features || [];
  }, [limits, effectiveRole]);

  // Check if user has access to a specific feature
  const hasFeature = (featureId: FeatureId): boolean => {
    // Guests have no features
    if (!user) return false;
    
    // Admins have all features
    if (effectiveRole === 'admin') return true;
    
    return userFeatures.includes(featureId);
  };

  // Check multiple features at once
  const hasAllFeatures = (featureIds: FeatureId[]): boolean => {
    return featureIds.every(id => hasFeature(id));
  };

  // Check if user has any of the specified features
  const hasAnyFeature = (featureIds: FeatureId[]): boolean => {
    return featureIds.some(id => hasFeature(id));
  };

  return {
    hasFeature,
    hasAllFeatures,
    hasAnyFeature,
    userFeatures,
    effectiveRole,
    isGuest: !user,
  };
};
